import React, { Fragment } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';

import FormattedDateNoTimeZone from 'components/Shared/FormattedDateNoTimeZone/FormattedDateNoTimeZone';
import PaymentAmountInput from 'components/Shared/Inputs/PaymentAmountInput';
import { getFormattedMessageId, getIntervalSentence } from 'components/Subscription/utils';
import {
  formatDateMMDDYYWithoutZeros,
  getDayNumber,
  getDayWithOrdinal,
  getMonthName,
  getMonthNumber,
  getOrdinal,
} from 'shared/dates';
import { capitalizeFirstLetter, lowercaseFirstLetter } from 'shared/utils';
import { colors, breakpoints, fontSize } from 'styles/cp';
import { SubscriptionInfoType } from 'types/Sale';

export interface Props {
  subscriptionInfo: SubscriptionInfoType;
  isOneTimeFee?: boolean;
  totalAmountWithoutOTF: number;
  isSubscriptionActive: boolean;
  currency: string;
  balanceAmount: string;
  payment: any;
  featureFlags: { [key: string]: any };
  isSignedIn: boolean;
  paymentAmountChange: () => any;
  paymentAmountReset: () => any;
  activeSubscriptionPaymentMethod?: string | false;
  region?: string;
}

const SubscriptionInvoiceHeader: React.FC<Props> = ({
  subscriptionInfo,
  isOneTimeFee,
  totalAmountWithoutOTF,
  isSubscriptionActive,
  currency,
  balanceAmount,
  payment = {},
  featureFlags = {},
  isSignedIn,
  paymentAmountChange,
  paymentAmountReset,
  activeSubscriptionPaymentMethod,
  region,
}) => {
  const {
    nextEventDate,
    startDateInstant,
    startDate,
    endDateInstant,
    expiryDateInstant,
    frequency,
    frequencyInterval,
    daysOfWeek,
    monthIndex,
    daysOfMonth,
    weekIndex,
  } = subscriptionInfo;

  const formatActivePaymentMethod = (pm: string) => {
    pm = pm.toLowerCase();
    pm = pm.charAt(0).toUpperCase() + pm.slice(1);
    return pm;
  };

  // Derive day, month, and week information from recurringInfo
  const dayInMonth =
    daysOfMonth && daysOfMonth?.length > 0
      ? getDayWithOrdinal(daysOfMonth[0])
      : startDate
      ? getDayWithOrdinal(getDayNumber(startDate))
      : '';

  const dayInWeek =
    daysOfWeek && daysOfWeek?.length > 0 ? capitalizeFirstLetter(daysOfWeek[0]) : '';

  const month = monthIndex
    ? getMonthName(monthIndex)
    : startDate
    ? getMonthName(getMonthNumber(startDate))
    : '';

  const dayNumber =
    daysOfMonth && daysOfMonth?.length ? daysOfMonth[0] : startDate ? getDayNumber(startDate) : '';

  const weekOrdinal = weekIndex ? getOrdinal(weekIndex) : '';

  const intl = useIntl();

  const formattedInterval = intl.formatMessage(
    {
      id: getFormattedMessageId({
        frequency,
        frequencyInterval,
        daysOfMonth,
        daysOfWeek,
      }),
      defaultMessage: getIntervalSentence({
        frequency,
        frequencyInterval,
        daysOfWeek,
        monthIndex,
        daysOfMonth,
        weekIndex,
        startDate,
      }),
    },
    {
      frequencyInterval,
      dayInMonth,
      dayInWeek,
      month,
      dayNumber,
      weekOrdinal,
    }
  );

  return (
    <Fragment>
      <div className="wrapper">
        <div className="dates-wrapper">
          {!isSubscriptionActive && (
            <span className="payment-amount-label">
              <FormattedMessage
                id={
                  isOneTimeFee
                    ? 'SUBSCRIPTION_FIRST_PAYMENT_AMOUNT'
                    : 'INVOICE_SUMMARY_MOBILE_PAYMENT_AMOUNT'
                }
                defaultMessage={isOneTimeFee ? 'first payment amount' : 'payment amount'}
              />
            </span>
          )}
        </div>

        <div className="payment-amount-wrapper">
          <PaymentAmountInput
            currency={currency}
            balanceAmount={balanceAmount}
            payment={payment as any}
            featureFlags={featureFlags}
            paymentAmountChange={paymentAmountChange}
            paymentAmountReset={paymentAmountReset}
            region={region}
          />
          <span className="interval-dates">
            {isOneTimeFee && (
              <FormattedMessage
                id="SUBSCRIPTION_OTF_THEN_AMOUNT_INTERVAL"
                defaultMessage="Then {amountWithoutOTF}, "
                values={{
                  amountWithoutOTF: (
                    <FormattedNumber
                      style="currency"
                      currency={currency}
                      value={totalAmountWithoutOTF}
                    />
                  ),
                }}
              />
            )}
            <span>
              {isOneTimeFee ? lowercaseFirstLetter(formattedInterval) : formattedInterval}
            </span>
          </span>
        </div>

        {!isSubscriptionActive && (
          <div className="dates">
            <span className="interval-dates">
              <FormattedMessage
                id={
                  endDateInstant
                    ? 'SUBSCRIPTION_INTERVAL_DATES'
                    : 'SUBSCRIPTION_INTERVAL_DATES_WITHOUT_END_DATE'
                }
                defaultMessage={`Starts {startDate}${endDateInstant ? ` and ends {endDate}` : ''}`}
                values={{
                  startDate: startDateInstant ? formatDateMMDDYYWithoutZeros(startDateInstant) : '',
                  endDate: endDateInstant ? formatDateMMDDYYWithoutZeros(endDateInstant) : '',
                }}
              />
            </span>
            <span className="accept-by-date">
              <FormattedMessage
                id="SUBSCRIPTION_ACCEPT_BY"
                defaultMessage="Accept by {expiryDate}"
                values={{
                  expiryDate: expiryDateInstant
                    ? formatDateMMDDYYWithoutZeros(expiryDateInstant)
                    : '',
                }}
              />
            </span>
          </div>
        )}

        {isSubscriptionActive && (
          <div className="details">
            <span>
              <FormattedMessage
                id="SUBSCRIPTION_NEXT_PAYMENT_DATE"
                defaultMessage={`Next recurring payment scheduled for`}
              />{' '}
              <FormattedDateNoTimeZone value={nextEventDate} />
            </span>
            {isSignedIn && activeSubscriptionPaymentMethod && (
              <div>
                <FormattedMessage id="PAYFLOW_PAYMENT_METHOD" defaultMessage="Payment method" />{' '}
                {formatActivePaymentMethod(activeSubscriptionPaymentMethod)}
              </div>
            )}
          </div>
        )}
      </div>
      <style jsx>{`
        .wrapper {
          color: ${colors.gray};
          font-family: AvenirNextforINTUIT-Regular;

          .dates-wrapper {
            @media screen and (max-width: ${breakpoints.sm}) {
              padding-bottom: 8px;
            }

            .header {
              display: inline-block;
              font-size: 20px;
              margin: 0;
              font-weight: 600;

              @media screen and (max-width: ${breakpoints.md}) {
                margin-top: 26px;
                font-size: 18px;
                padding-right: 4px;
              }
            }

            .payment-amount-label {
              font-family: AvenirNextforINTUIT-Demi;
              font-size: ${fontSize.xxs};
              color: ${colors.whiteGray};
              text-transform: uppercase;
              display: inline-block;

              @media screen and (max-width: ${breakpoints.md}) {
                margin-top: 40px;
              }
            }

            .subheader {
              margin: 0;
              display: block;
              padding-top: 4px;
              font-family: AvenirNextforINTUIT-Regular;
              font-size: 14px;
            }
          }

          .details {
            margin-top: 8px;
            font-size: 14px;
            line-height: 20px;
            font-weight: 500;
          }

          .dates {
            margin-top: ${isOneTimeFee ? '4' : '12'}px;
            display: flex;
            flex-direction: column;
            gap: 4px;
          }

          .payment-amount-wrapper {
            display: flex;
            gap: 8px;
            align-items: baseline;
            flex-direction: ${isOneTimeFee ? 'column' : 'row'};

            @media screen and (max-width: ${breakpoints.sm}) {
              flex-direction: column;
            }
          }

          .interval-dates {
            font-size: ${fontSize.xs};
            font-weight: 500;
          }

          .accept-by-date {
            font-size: ${fontSize.xxs};
            color: ${colors.gray02};
          }
        }
      `}</style>
    </Fragment>
  );
};

export default SubscriptionInvoiceHeader;
